import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DndContext, 
  PointerSensor, 
  useSensor, 
  useSensors, 
  closestCenter 
} from '@dnd-kit/core';
import { 
  SortableContext, 
  rectSortingStrategy 
} from '@dnd-kit/sortable';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboards, getDashboardWithWidgets, deleteWidget, updateWidgetPositions } from '../../services/dashboardApi';
import DashboardWidget from './DashboardWidget';
import GraphModal from '../ChatDrawer/GraphModal';
import { logger } from '../../utils/logger';
import { readSelectedDashboardId, writeSelectedDashboardId } from '../../utils/workspaceState';
import './DashboardView.css';

/**
 * Stored dashboard id when that dashboard is in the list, otherwise the first id.
 * @param {Array} dashboardList
 * @param {string} [userId]
 * @param {string|number} [companyId]
 * @returns {number|string}
 */
function dashboardIdFromList(dashboardList, userId, companyId) {
  const storedId = readSelectedDashboardId(userId, companyId);
  const match = storedId != null
    ? dashboardList.find((dashboard) => Number(dashboard.id) === storedId)
    : null;
  return match ? match.id : dashboardList[0].id;
}

/**
 * Main dashboard view component
 * Displays dashboards and their widgets
 * @param {Object} props - Component props
 * @returns {JSX.Element} Dashboard view component
 */
const DashboardView = () => {
  const { t } = useTranslation();
  const { activeCompany, user } = useAuth();
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeletingWidget, setIsDeletingWidget] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [selectedWidgetData, setSelectedWidgetData] = useState(null);

  // Setup drag sensors (require 5px movement to start drag)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Load dashboards on mount or when company changes
  useEffect(() => {
    if (!activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const run = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const dashboardList = await getDashboards(activeCompany.id, signal);
        if (signal.aborted) return;

        setDashboards(dashboardList);

        if (dashboardList.length > 0) {
          const dashboard = await getDashboardWithWidgets(
            dashboardIdFromList(dashboardList, user?.id, activeCompany.id),
            activeCompany.id,
            signal
          );
          if (signal.aborted) return;
          setSelectedDashboard(dashboard);
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Failed to load dashboards:', err);
        setError(err.message || t('dashboards.error'));
      } finally {
        if (!signal.aborted) setIsLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [activeCompany?.id, user?.id]);

  const loadDashboards = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const dashboardList = await getDashboards(activeCompany.id);
      setDashboards(dashboardList);

      if (dashboardList.length > 0) {
        await loadDashboard(dashboardIdFromList(dashboardList, user?.id, activeCompany.id));
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      logger.error('Failed to load dashboards:', err);
      setError(err.message || t('dashboards.error'));
      setIsLoading(false);
    }
  };

  const loadDashboard = async (dashboardId) => {
    setIsLoading(true);
    setError(null);

    try {
      const dashboard = await getDashboardWithWidgets(dashboardId, activeCompany.id);
      setSelectedDashboard(dashboard);
    } catch (err) {
      logger.error('Failed to load dashboard:', err);
      setError(err.message || t('dashboards.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDashboardChange = (e) => {
    const dashboardId = Number(e.target.value);
    if (!Number.isInteger(dashboardId)) return;
    writeSelectedDashboardId(user?.id, activeCompany?.id, dashboardId);
    loadDashboard(dashboardId);
  };

  const handleWidgetDelete = async (widgetId, widgetName) => {
    setIsDeletingWidget(true);
    setError(null);

    try {
      await deleteWidget(widgetId, activeCompany.id);
      
      // Reload the dashboard to get updated widget list
      if (selectedDashboard) {
        await loadDashboard(selectedDashboard.id);
      }
      
      logger.info(`Widget deleted: ${widgetName} (id=${widgetId})`);
    } catch (err) {
      logger.error('Failed to delete widget:', err);
      setError(err.message || t('dashboards.error'));
    } finally {
      setIsDeletingWidget(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeWidget = active.data.current.widget;
    const overWidget = over.data.current.widget;

    if (!activeWidget || !overWidget) {
      logger.error('Could not find widgets for drag operation');
      return;
    }

    logger.info('Drag end:', {
      activeWidget: { id: activeWidget.id, row: activeWidget.row, col: activeWidget.column },
      overWidget: { id: overWidget.id, row: overWidget.row, col: overWidget.column }
    });

    // Swap positions
    const updates = [
      {
        widget_id: activeWidget.id,
        row: overWidget.row,
        column: overWidget.column
      },
      {
        widget_id: overWidget.id,
        row: activeWidget.row,
        column: activeWidget.column
      }
    ];

    logger.info('Sending position updates:', updates);

    try {
      // Optimistically update UI immediately for smooth UX
      const updatedWidgets = selectedDashboard.widgets.map(widget => {
        if (widget.id === activeWidget.id) {
          return { ...widget, row: overWidget.row, column: overWidget.column };
        } else if (widget.id === overWidget.id) {
          return { ...widget, row: activeWidget.row, column: activeWidget.column };
        }
        return widget;
      });

      setSelectedDashboard({
        ...selectedDashboard,
        widgets: updatedWidgets
      });

      // Persist to backend
      const result = await updateWidgetPositions(selectedDashboard.id, updates, activeCompany.id);
      
      logger.info('Backend updated, result:', result);
      logger.info(`Swapped widgets ${activeWidget.id} and ${overWidget.id} - optimistic update confirmed`);
      
      // No reload needed on success - trust the optimistic update!
      // This keeps the UX smooth without unnecessary re-fetches
    } catch (err) {
      logger.error('Failed to swap widgets:', err);
      setError(err.message || 'Failed to reorder widgets');
      // Only reload on error to revert the optimistic update
      await loadDashboard(selectedDashboard.id);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleWidgetClick = (widgetData) => {
    setSelectedWidgetData(widgetData);
    setIsGraphModalOpen(true);
  };

  const handleCloseGraphModal = () => {
    setIsGraphModalOpen(false);
    setSelectedWidgetData(null);
  };

  // Parse structure to get grid dimensions
  const getGridDimensions = () => {
    if (!selectedDashboard) return { rows: 2, cols: 2 };
    
    const [rows, cols] = selectedDashboard.structure.split('x').map(Number);
    return { rows, cols };
  };

  // Empty state when no dashboards exist
  if (!isLoading && dashboards.length === 0) {
    return (
      <div className="dashboard-view">
        <div className="empty-dashboard-state">
          <div className="empty-icon">
            <img src="/dashboard_icon.svg" alt="Dashboard" />
          </div>
          <h3>{t('dashboards.noDashboards')}</h3>
          <p>{t('dashboards.emptyMessage')}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="dashboard-view">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>{t('dashboards.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-view">
        <div className="dashboard-error">
          <p>{error}</p>
          <button className="retry-btn" onClick={loadDashboards}>
            {t('dashboards.retry')}
          </button>
        </div>
      </div>
    );
  }

  const { rows, cols } = getGridDimensions();

  return (
    <div className="dashboard-view">
      {/* Dashboard Selector (future-proof for multiple dashboards) */}
      {dashboards.length > 1 && (
        <div className="dashboard-header">
          <div className="dashboard-selector">
            <select
              value={selectedDashboard?.id || ''}
              onChange={handleDashboardChange}
              className="dashboard-select"
            >
              {dashboards.map((dashboard) => (
                <option key={dashboard.id} value={dashboard.id}>
                  {dashboard.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Dashboard Content */}
      {selectedDashboard && (
        <div className="dashboard-content">
          {selectedDashboard.widgets && selectedDashboard.widgets.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              key={`dnd-${selectedDashboard.id}-${selectedDashboard.updated_at || Date.now()}`}
            >
              <SortableContext
                items={selectedDashboard.widgets.map(w => w.id)}
                strategy={rectSortingStrategy}
              >
                <div 
                  className="dashboard-grid" 
                  data-structure={selectedDashboard.structure}
                  style={{
                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                    gridTemplateRows: `repeat(${rows}, 1fr)`
                  }}
                >
                  {selectedDashboard.widgets.map((widget) => (
                    <DashboardWidget
                      key={widget.id}
                      widget={widget}
                      onDelete={handleWidgetDelete}
                      onWidgetClick={handleWidgetClick}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="empty-dashboard">
              <div className="empty-icon">
                <img src="/dashboard_icon.svg" alt="Dashboard" />
              </div>
              <h3>{t('dashboards.emptyDashboard')}</h3>
              <p>{t('dashboards.emptyMessage')}</p>
            </div>
          )}
        </div>
      )}

      {/* Graph Modal for widget full-screen view */}
      {isGraphModalOpen && selectedWidgetData && (
        <GraphModal
          isOpen={isGraphModalOpen}
          onClose={handleCloseGraphModal}
          chartData={selectedWidgetData.chartData}
          queryId={selectedWidgetData.queryId}
          selectedChartType={selectedWidgetData.selectedChartType}
        />
      )}
    </div>
  );
};

export default DashboardView;

